import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Eye, Target } from "lucide-react";

const KOProblemSection = () => {
  const barRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(barRef, { once: true, margin: "-100px" });
  const [budgetPercent, setBudgetPercent] = useState(100);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setBudgetPercent(8), 300);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  const problems = [
    {
      icon: <DollarSign className="h-8 w-8" />,
      title: "Budget Exhaustion",
      description:
        "Your entire daily budget gets consumed by 1-2 clicks on expensive, competitive keywords. By mid-morning, your ads stop showing entirely.",
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Zero Visibility",
      description:
        "With budget gone before lunch, your business is invisible for the remaining 14+ hours of the day. Potential customers searching in the afternoon never see your ads.",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Poor Ad Relevance",
      description:
        "Generic, expensive keywords attract broad searchers who aren't ready to convert. You're paying premium prices for low-intent traffic that rarely becomes a customer.",
    },
  ];

  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <Badge className="px-3 py-1 text-sm bg-red-100 text-red-700 border-red-200 mb-4">
              The Problem
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Most Advertisers Burn Their Budget by 10 AM
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              The default Google Ads approach is to bid on high-volume, competitive keywords.
              But when your daily budget is $30-$100, a single click on a $40 keyword wipes you out.
              Your campaigns go dark while competitors with bigger budgets stay visible all day.
            </p>
          </motion.div>
        </div>

        {/* Animated budget drain bar */}
        <motion.div
          ref={barRef}
          className="max-w-2xl mx-auto mb-16 bg-white rounded-xl p-6 border border-border shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold">Daily Budget: $50.00</span>
            <span className="text-sm font-bold text-red-600">
              {isInView ? "1 click on 'roofing company' — $45.10" : "Budget: $50.00"}
            </span>
          </div>
          <div className="h-10 bg-muted rounded-full overflow-hidden relative">
            <div
              className="h-full rounded-full transition-all duration-[2000ms] ease-out flex items-center justify-end pr-3"
              style={{
                width: `${budgetPercent}%`,
                background: budgetPercent > 50
                  ? "linear-gradient(90deg, #22c55e, #eab308)"
                  : budgetPercent > 20
                  ? "linear-gradient(90deg, #eab308, #f97316)"
                  : "linear-gradient(90deg, #ef4444, #dc2626)",
              }}
            >
              {budgetPercent > 15 && (
                <span className="text-xs font-bold text-white">
                  ${(50 * budgetPercent / 100).toFixed(2)} left
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-muted-foreground">9:00 AM — Campaign starts</span>
            <span className="text-xs text-red-500 font-medium">
              {isInView ? "10:15 AM — Budget exhausted" : ""}
            </span>
          </div>
        </motion.div>

        {/* Problem cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              className="bg-white rounded-xl p-6 border border-border hover:border-red-200 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className="w-14 h-14 rounded-lg bg-red-100 text-red-600 flex items-center justify-center mb-4">
                {problem.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{problem.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>

        {/* SEO body text */}
        <motion.div
          className="mt-16 max-w-4xl mx-auto text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-muted-foreground leading-relaxed">
            This isn't just a minor inefficiency — it's the most common reason small and medium businesses
            fail with Google Ads. Research shows that advertisers with daily budgets under $100 waste an
            average of 40-60% of their spend on keywords that are too expensive to generate positive ROI.
            The solution isn't to increase your budget — it's to be smarter about which keywords you target.
            Budget-based keyword optimisation ensures every pound or dollar of your ad spend goes toward
            keywords that can realistically drive conversions within your financial constraints.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default KOProblemSection;
